import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  inject,
  OnChanges,
  SimpleChanges,
  TemplateRef,
} from '@angular/core';
import { AlertController, IonicModule, PopoverController, ModalController } from '@ionic/angular';
import { Editor, Extension } from '@tiptap/core';
import Link from '@tiptap/extension-link';
import Mention from '@tiptap/extension-mention';
import Placeholder from '@tiptap/extension-placeholder';
import StarterKit from '@tiptap/starter-kit';
import _ from 'lodash';
import { NgxTippyModule, NgxTippyService } from 'ngx-tippy-wrapper';

import { SUPPORT_STEP_ASSIGNEE_TYPES } from 'src/app/constants';
import { CustomEmoji } from 'src/app/utils/tip-tap-extensions/custom-emoji';
import { Variable } from 'src/app/utils/tip-tap-extensions/variable';
import { Profile, SlackChannel, WhereClause } from 'src/models';
import { AuthService } from 'src/services/auth.service';
import { SearchablePopoverService } from 'src/services/searchable-popover.service';
import { SlackUserGroupsService } from 'src/services/slack-user-groups.service';
import { SlackService } from 'src/services/slack.service';
import { UsersService } from 'src/services/users.service';

import { AvatarModule } from '../avatar/avatar.module';
import { EmojiPickerComponent } from '../emoji-picker/emoji-picker.component';
import { VariableConfigModalComponent } from '../variable-config-modal/variable-config-modal.component';

const RAW_MENTION_TYPES = {
  USER: 'user-mention',
  TEAM: 'slack-usergroup-mention',
  CHANNEL: 'slack-channel-mention',
} as const;

const MENTION_TYPES = {
  USER: 'user',
  TEAM: 'team',
  CHANNEL: 'channel',
} as const;

export interface EditorVariableDefinition {
  path: string;
  label: string;
  type?: 'link' | (typeof RAW_MENTION_TYPES)[keyof typeof RAW_MENTION_TYPES] | 'emoji' | 'markdown';
}

function formatUserName(user: Partial<Profile>, connectedTeams: any[]) {
  return `${user.name}${connectedTeams?.length > 1 && user.slackTeam?.name ? ` (${user.slackTeam.name})` : ''}`;
}

// Custom Mention extension that can parse existing mention HTML
const CustomMention = Mention.extend({
  addAttributes() {
    return {
      type: {
        default: 'user',
        parseHTML: (element) =>
          element.getAttribute('data-type') === RAW_MENTION_TYPES.TEAM
            ? MENTION_TYPES.TEAM
            : element.getAttribute('data-type') === RAW_MENTION_TYPES.CHANNEL
              ? MENTION_TYPES.CHANNEL
              : MENTION_TYPES.USER,
        renderHTML: (attributes) => {
          if (!attributes.type) {
            return {};
          }

          return { 'data-type': attributes.type };
        },
      },
      slackId: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-slack-id'),
        renderHTML: (attributes) => {
          if (!attributes.slackId) {
            return {};
          }

          return { 'data-slack-id': attributes.slackId };
        },
      },
      unthreadId: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-id'),
        renderHTML: (attributes) => {
          if (!attributes.id) {
            return {};
          }

          return { 'data-id': attributes.id };
        },
      },
      id: {
        default: '',
        parseHTML: (element) => element.getAttribute('id'),
        renderHTML: () => {
          return {};
        },
      },
      label: {
        default: '',
        parseHTML: (element) =>
          (element.getAttribute('data-label') ?? element.textContent ?? '').replace('@', '').replace('#', ''),
        renderHTML: (attributes) => {
          if (!attributes.label) {
            return {};
          }

          return { 'data-label': attributes.label };
        },
      },
    };
  },
  parseHTML() {
    return [
      {
        tag: `span[data-type="${RAW_MENTION_TYPES.USER}"]`,
        getAttrs: (element: HTMLElement) => {
          const slackId = element.getAttribute('data-slack-id');
          const unthreadId = element.getAttribute('data-id');
          const textContent = element.textContent || '';
          const label = textContent.replace('@', '');

          if (!unthreadId) {
            return false;
          }

          return {
            id: `${unthreadId}|${slackId ?? ''}|${MENTION_TYPES.USER}`,
            label: label,
          };
        },
      },
      {
        tag: `span[data-type="${RAW_MENTION_TYPES.TEAM}"]`,
        getAttrs: (element: HTMLElement) => {
          const slackId = element.getAttribute('data-slack-id');
          const textContent = element.textContent || '';
          const label = textContent.replace('@', '');

          if (!slackId) {
            return false;
          }

          // Usergroups only have Slack IDs, no Unthread IDs
          return {
            id: `|${slackId}|${MENTION_TYPES.TEAM}`,
            label: label,
          };
        },
      },
      {
        // Parse channel mentions
        tag: `span[data-type="${RAW_MENTION_TYPES.CHANNEL}"]`,
        getAttrs: (element: HTMLElement) => {
          const channelId = element.getAttribute('data-slack-id') || element.getAttribute('data-id');
          const textContent = element.textContent || '';
          const label = textContent.replace('#', '');

          if (!channelId) {
            return false;
          }

          // Channels only have Slack IDs, no Unthread IDs
          return {
            id: `|${channelId}|${MENTION_TYPES.CHANNEL}`,
            label: label,
          };
        },
      },
      {
        // Also parse the new format we're generating
        tag: 'span[data-type="mention"]',
        getAttrs: (element: HTMLElement) => {
          const id = element.getAttribute('data-id');
          const label = element.getAttribute('data-label');

          if (!id || !label) return false;

          return {
            id: id,
            label: label,
          };
        },
      },
    ];
  },
});

@Component({
  standalone: true,
  imports: [CommonModule, IonicModule, VariableConfigModalComponent, NgxTippyModule, AvatarModule],
  selector: 'app-slack-text-editor',
  templateUrl: './slack-text-editor.component.html',
  styleUrls: ['./slack-text-editor.component.css'],
})
export class SlackTextEditorComponent implements OnInit, OnChanges {
  @Input() content: string;
  @Input() isRichText = false;
  @Input() placeholder: string;
  @Input() hideStrikethrough: boolean;
  @Input() hideCodeblock: boolean;
  @Input() hideQuote: boolean;
  @Input() hideCode: boolean;
  @Input() hideVariable: boolean;
  @Input() projectId?: string | null;
  @Input() enableSlackMentions = false;
  @Input() teamId?: string;
  @Output() contentDidChange = new EventEmitter();
  @Output() didBlur = new EventEmitter();
  @Output() didFocus = new EventEmitter();
  @ViewChild('editorWrapper', { static: true }) editorWrapper;
  @ViewChild('editorTooltipTemplate', { static: false })
  editorTooltipTemplate: TemplateRef<any>;

  private editor: Editor;

  // Slack mentions state (only used when enableSlackMentions is true)
  public autocompleteSearchMode: 'users' | 'channels' | 'teams' | 'none' = 'none';
  public taggableUsers: Profile[];
  public taggableChannels: Partial<SlackChannel>[];
  public taggableTeams: any[] = [];
  public selectedTeamForMentions: any = null;
  public showingTeamSelection = false;

  // Workspace selection for user groups
  public availableWorkspacesForUsers: any[] = [];
  public selectedWorkspaceForUsers: any = null;
  public showingWorkspaceSelectionForUsers = false;
  highlightedItemIndex = -1;
  keyCommandStartIndex: number;
  taggableRange: { from: number; to: number };
  tippyName = 'slack-editor-tippy';
  tippyProps: any = {};
  lastRequestId = 0;
  connectedTeams: any[];

  constructor(
    private searchablePopoverService: SearchablePopoverService,
    private modalCtrl: ModalController,
    private popoverCtrl: PopoverController,
    private alertCtrl: AlertController,
    private tippyService: NgxTippyService,
    private usersService: UsersService,
    private slackService: SlackService,
    private slackUserGroupsService: SlackUserGroupsService,
    private authService: AuthService,
  ) {}

  emitChange() {
    this.contentDidChange.next(this.editor.getHTML());
  }

  async ngOnInit() {
    // Setup tippy configuration if Slack mentions are enabled
    if (this.enableSlackMentions) {
      const randomString = crypto.randomUUID();
      this.tippyName = `slack-editor-tippy-${randomString}`;

      this.tippyProps = {
        trigger: 'manual',
        interactive: true,
        arrow: false,
        placement: 'bottom-start',
        appendTo: () => document.body,
        zIndex: 10000,
        popperOptions: {
          strategy: 'fixed',
          modifiers: [
            {
              name: 'preventOverflow',
              options: {
                boundary: 'viewport',
                altAxis: true,
                padding: 8,
              },
            },
            {
              name: 'flip',
              options: {
                fallbackPlacements: ['top-start', 'bottom-end', 'top-end'],
              },
            },
          ],
        },
      };

      this.connectedTeams = await this.slackService.getConnectedTeams();
    }

    const extensions: any[] = [
      StarterKit.configure({
        horizontalRule: false,
        heading: false,
        orderedList: this.isRichText
          ? {
              HTMLAttributes: {
                class: 'slack-rich-text-list',
              },
            }
          : false,
        bulletList: this.isRichText
          ? {
              HTMLAttributes: {
                class: 'slack-rich-text-list',
              },
            }
          : false,
      }),
      CustomEmoji.configure({
        inline: true,
      }),
      Link.configure({
        linkOnPaste: true,
      }),
      Placeholder.configure({
        placeholder: this.placeholder,
      }),
      Variable,
    ];

    // Add mentions functionality if enabled
    if (this.enableSlackMentions) {
      const context = this;

      extensions.push(
        CustomMention.configure({
          HTMLAttributes: {
            class: 'mention-tag',
          },
          renderHTML({ options, node }) {
            let [unthreadId, slackId, type] = node.attrs.id?.split('|') ?? [];

            if (!unthreadId) {
              unthreadId = node.attrs.unthreadId;
            }

            if (!slackId) {
              slackId = node.attrs.slackId;
            }

            if (!type) {
              type = node.attrs.type;
            }

            let dataType: (typeof RAW_MENTION_TYPES)[keyof typeof RAW_MENTION_TYPES] = RAW_MENTION_TYPES.USER;
            let prefix = options.suggestion.char;

            if (type === MENTION_TYPES.TEAM) {
              dataType = RAW_MENTION_TYPES.TEAM;
            } else if (type === MENTION_TYPES.CHANNEL) {
              dataType = RAW_MENTION_TYPES.CHANNEL;
              prefix = '#';
            }

            const attrs = {
              'data-type': dataType,
              class: 'mention-tag',
            };

            if (slackId) {
              attrs['data-slack-id'] = slackId;
            }

            // Only add data-id if it exists (users have it, groups/channels don't)
            if (unthreadId) {
              attrs['data-id'] = unthreadId;
            }

            return ['span', attrs, `${prefix}${node.attrs.label ?? slackId ?? ''}`];
          },
          suggestion: {
            render: () => {
              return {
                onStart: (props: any) => {
                  context.searchTaggableUsers('', true);
                  context.tippyService
                    .getInstance(context.tippyName)
                    .setProps({ getReferenceClientRect: props.clientRect });
                  context.tippyService.show(context.tippyName);
                  context.taggableRange = props.range;
                },
                onUpdate(props) {
                  context.searchTaggableUsers(props.query, true);
                  context.tippyService
                    .getInstance(context.tippyName)
                    .setProps({ getReferenceClientRect: props.clientRect });
                  context.tippyService.show(context.tippyName);
                  context.taggableRange = props.range;
                },
                onKeyDown(props) {
                  context.onKeyDown.apply(context, [props.event.key, 'editor']);
                  const specialKeys = ['Enter', 'Escape', 'ArrowDown', 'ArrowUp'];
                  if (specialKeys.includes(props.event.key)) {
                    return true;
                  }
                },
                onExit() {
                  context.hideAutocompleteTippy();
                },
              };
            },
          },
        }),
      );

      // Add keyboard shortcuts for channel mentions
      extensions.push(
        Extension.create({
          addKeyboardShortcuts() {
            const shortcuts: any = {};

            shortcuts['@'] = (props) => {
              const anchorIndex = props.editor.state.selection.anchor;
              // Trigger only if @ starts the message, or is preceded by a space
              if (
                anchorIndex === 1 ||
                props.editor.state.doc.textBetween(anchorIndex - 1, anchorIndex) === ' ' ||
                props.editor.state.doc.textBetween(anchorIndex - 1, anchorIndex) === ''
              ) {
                context.keyCommandStartIndex = anchorIndex;
                context.searchTaggableUsers('', true);
              }
              return false;
            };

            shortcuts['#'] = (props) => {
              const anchorIndex = props.editor.state.selection.anchor;
              // Trigger only if # starts the message, or is preceded by a space
              if (
                anchorIndex === 1 ||
                props.editor.state.doc.textBetween(anchorIndex - 1, anchorIndex) === ' ' ||
                props.editor.state.doc.textBetween(anchorIndex - 1, anchorIndex) === ''
              ) {
                context.keyCommandStartIndex = anchorIndex;
                context.searchChannels('');
              }
              return false;
            };

            shortcuts['ArrowDown'] = () => {
              if (context.autocompleteSearchMode !== 'none') {
                context.onKeyDown('ArrowDown', 'editor');
                return true;
              }
              return false;
            };

            shortcuts['ArrowUp'] = () => {
              if (context.autocompleteSearchMode !== 'none') {
                context.onKeyDown('ArrowUp', 'editor');
                return true;
              }
              return false;
            };

            shortcuts['Enter'] = () => {
              if (context.autocompleteSearchMode !== 'none') {
                context.onKeyDown('Enter', 'editor');
                return true;
              }
              return false;
            };

            shortcuts['Escape'] = () => {
              context.hideAutocompleteTippy();
              return true;
            };

            return shortcuts;
          },
        }),
      );
    }

    this.editor = new Editor({
      element: this.editorWrapper.nativeElement,
      extensions,
      content: this.content,
      injectCSS: true,
      onUpdate: () => {
        this.emitChange();
        if (this.enableSlackMentions) {
          this.handleSlackMentionSearch();
        }
      },
      onBlur: () => {
        this.didBlur.emit();
      },
      onFocus: () => {
        this.didFocus.emit();
      },
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    // Handle content changes after editor is initialized
    if (changes['content'] && this.editor && !this.editor.isDestroyed) {
      const newContent = changes['content'].currentValue;
      const currentContent = this.editor.getHTML();

      // Only update if content actually changed to avoid unnecessary updates
      if (newContent !== currentContent) {
        // Use setContent with emitUpdate: false to avoid triggering onUpdate
        // This ensures TipTap parses the HTML properly instead of treating it as plain text
        this.editor.commands.setContent(newContent || '', false);
      }
    }
  }

  clearFormatting() {
    this.editor.chain().focus().clearNodes().unsetAllMarks().run();
    this.emitChange();
  }

  toggleBold() {
    this.editor.chain().focus().toggleBold().run();
    this.emitChange();
  }

  toggleItalic() {
    this.editor.chain().focus().toggleItalic().run();
    this.emitChange();
  }

  toggleBulletList() {
    this.editor.chain().focus().toggleBulletList().run();
    this.emitChange();
  }

  toggleOrderedList() {
    this.editor.chain().focus().toggleOrderedList().run();
    this.emitChange();
  }

  toggleStrikethrough() {
    this.editor.chain().focus().toggleStrike().run();
    this.emitChange();
  }

  toggleBlockquote() {
    this.editor.chain().focus().toggleBlockquote().run();
    this.emitChange();
  }

  toggleCodeBlock() {
    this.editor.chain().focus().toggleCodeBlock().run();
    this.emitChange();
  }

  toggleInlineCode() {
    this.editor.chain().focus().toggleCode().run();
    this.emitChange();
  }

  async insertVariable() {
    const modal = await this.modalCtrl.create({
      component: VariableConfigModalComponent,
      cssClass: 'auto-height modal-sm',
      componentProps: {
        projectId: this.projectId,
      },
    });

    await modal.present();

    const { data } = await modal.onDidDismiss();

    if (data) {
      this.insertConfiguredVariable(data);
    }
  }

  insertConfiguredVariable(configData: any) {
    if (configData.type === 'link') {
      this.editor
        .chain()
        .focus()
        .insertVariableLink({
          path: configData.path,
          text: configData.text,
          href: '#',
          label: configData.label,
        })
        .insertContent(' ')
        .run();
    } else {
      this.editor
        .chain()
        .focus()
        .insertVariable({
          path: configData.path,
          format: configData.type,
          label: configData.label,
          fallbackText: configData.fallbackText,
        })
        .insertContent(' ')
        .run();
    }

    this.emitChange();
  }

  async insertEmoji() {
    const popover = await this.popoverCtrl.create({
      component: EmojiPickerComponent,
      componentProps: {
        callback: (emoji: any) => {
          popover.dismiss();

          if (emoji.native) {
            this.editor.commands.insertContent(emoji.native, {
              updateSelection: true,
              parseOptions: {
                preserveWhitespace: 'full',
              },
            });
          } else if (emoji.src) {
            this.editor.commands.insertCustomEmoji({
              name: emoji.name,
              src: emoji.src,
            });
          }

          this.emitChange();
        },
      },
      showBackdrop: false,
      cssClass: 'emoji-picker-popover',
      event,
    });

    popover.present();
  }

  async addLink() {
    const alert = await this.alertCtrl.create({
      header: 'Enter a link',
      inputs: [
        {
          name: 'link',
          placeholder: 'https://',
        },
      ],
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
        },
        {
          text: 'Add',
          handler: (data) => {
            this.editor.chain().focus().setLink({ href: data.link }).run();
            this.emitChange();
          },
        },
      ],
    });

    alert.present();
  }

  // Slack mentions functionality (only used when enableSlackMentions is true)

  private handleSlackMentionSearch() {
    // This method handles mention search during editor updates
    // Similar to reply composer's onUpdate logic
    if (this.autocompleteSearchMode === 'users') {
      if (this.keyCommandStartIndex <= this.editor.state.selection.anchor) {
        // increase start index by one to skip over the '@' character
        const search = (
          this.editor.state.doc.textBetween(this.keyCommandStartIndex + 1, this.editor.state.selection.anchor) ?? ''
        ).toLowerCase();

        this.searchTaggableUsers(search, true);
      } else {
        this.hideAutocompleteTippy();
      }
    } else if (this.autocompleteSearchMode === 'channels') {
      if (this.keyCommandStartIndex <= this.editor.state.selection.anchor) {
        // increase start index by one to skip over the '#' character
        const search = (
          this.editor.state.doc.textBetween(this.keyCommandStartIndex + 1, this.editor.state.selection.anchor) ?? ''
        ).toLowerCase();

        this.searchChannels(search);
      } else {
        this.hideAutocompleteTippy();
      }
    } else if (this.autocompleteSearchMode === 'teams') {
      if (this.keyCommandStartIndex <= this.editor.state.selection.anchor) {
        // increase start index by one to skip over the '#' character
        const search = (
          this.editor.state.doc.textBetween(this.keyCommandStartIndex + 1, this.editor.state.selection.anchor) ?? ''
        ).toLowerCase();

        this.searchTeams(search);
      } else {
        this.hideAutocompleteTippy();
      }
    }
  }

  onKeyDown(key: string, source: 'editor') {
    const list =
      this.autocompleteSearchMode === 'users'
        ? this.showingWorkspaceSelectionForUsers
          ? this.availableWorkspacesForUsers
          : this.taggableUsers
        : this.autocompleteSearchMode === 'channels'
          ? this.taggableChannels
          : this.autocompleteSearchMode === 'teams'
            ? this.taggableTeams
            : [];

    if (key === 'Enter') {
      if (this.autocompleteSearchMode === 'users') {
        if (this.showingWorkspaceSelectionForUsers) {
          this.selectWorkspaceForUsers(this.availableWorkspacesForUsers[this.highlightedItemIndex]);
        } else {
          this.selectTaggedUser(this.taggableUsers[this.highlightedItemIndex]);
        }
      } else if (this.autocompleteSearchMode === 'channels') {
        this.selectTaggedChannel(this.taggableChannels[this.highlightedItemIndex]);
      } else if (this.autocompleteSearchMode === 'teams') {
        this.selectTeamForMentions(this.taggableTeams[this.highlightedItemIndex]);
      } else {
        this.hideAutocompleteTippy();
      }
    } else if (key === 'Escape') {
      if (this.showingTeamSelection) {
        this.hideAutocompleteTippy();
      } else if (this.showingWorkspaceSelectionForUsers) {
        this.hideAutocompleteTippy();
      } else if (
        this.autocompleteSearchMode === 'users' &&
        this.connectedTeams?.length > 1 &&
        this.selectedWorkspaceForUsers
      ) {
        // Go back to workspace selection for user groups
        this.goBackToWorkspaceSelectionForUsers();
      } else if (this.autocompleteSearchMode === 'channels' && this.connectedTeams?.length > 1) {
        this.goBackToTeamSelection();
      } else {
        this.hideAutocompleteTippy();
      }
    } else if (key === 'ArrowDown') {
      if (this.highlightedItemIndex < -1 || this.highlightedItemIndex > list.length) {
        this.highlightedItemIndex = -1;
      }

      this.highlightedItemIndex++;

      if (this.highlightedItemIndex >= list?.length) {
        this.highlightedItemIndex = 0;
      }
    } else if (key === 'ArrowUp') {
      this.highlightedItemIndex--;

      if (this.highlightedItemIndex < 0 && list?.length) {
        this.highlightedItemIndex = list.length - 1;
      }
    }
  }

  hideAutocompleteTippy() {
    this.autocompleteSearchMode = 'none';
    this.showingTeamSelection = false;
    this.selectedTeamForMentions = null;

    // Reset workspace selection for user groups
    this.showingWorkspaceSelectionForUsers = false;
    this.selectedWorkspaceForUsers = null;

    // Ensure complete cleanup of tippy tooltip
    this.tippyService.hide(this.tippyName);

    // Reset all data arrays to ensure template conditions work properly
    this.taggableUsers = [];
    this.taggableChannels = [];
    this.taggableTeams = [];
    this.availableWorkspacesForUsers = [];
    this.highlightedItemIndex = -1;
  }

  async searchTaggableUsers(searchText: string, includeTeams: boolean) {
    if (!this.enableSlackMentions) {
      return;
    }

    // If multiple teams and no workspace selected yet for user groups, show workspace selection first
    if (includeTeams && this.connectedTeams?.length > 1 && !this.selectedWorkspaceForUsers) {
      this.searchWorkspacesForUsers(searchText);
      return;
    }

    this.autocompleteSearchMode = 'users';
    this.showingWorkspaceSelectionForUsers = false;

    const usersWhere: WhereClause[] = [
      {
        field: 'slackIds',
        operator: 'notIn',
        value: [null, []],
      },
      {
        field: 'status',
        operator: '==',
        value: 'active',
      },
      {
        field: 'name',
        operator: '!=',
        value: null,
      },
    ];

    const userTeamsWhere: WhereClause[] = [
      {
        field: 'name',
        operator: '!=',
        value: null,
      },
      {
        field: 'slackId',
        operator: '!=',
        value: null,
      },
    ];

    // Determine which team ID to use for filtering
    let effectiveTeamId: string | undefined;

    if (includeTeams && this.selectedWorkspaceForUsers) {
      // Use selected workspace for user groups
      effectiveTeamId = this.selectedWorkspaceForUsers.id;
    } else {
      // Use default team logic
      effectiveTeamId = this.teamId || this.authService.tenant?.slackTeamId;
    }

    const slackTeam = effectiveTeamId ? await this.slackService.getTeam(effectiveTeamId) : undefined;

    if (effectiveTeamId) {
      userTeamsWhere.push({
        field: 'slackTeamId',
        operator: '==',
        value: effectiveTeamId,
      });
    }

    this.lastRequestId++;
    const requestId = this.lastRequestId;

    const data = [];

    // Prepare search parameters - don't pass search if empty to get all results
    const userSearchParams: any = {
      includeExternalUsers: false,
      internalUsersSlackOrgId: slackTeam?.slackEnterpriseId ?? effectiveTeamId ?? undefined,
      where: usersWhere,
      order: ['name'],
      descending: false,
      limit: 20,
    };

    // Only add search if we have text to search for
    if (searchText && searchText.trim()) {
      userSearchParams.search = searchText;
    }

    const teamSearchParams: any = {
      limit: 20,
      order: ['name'],
      descending: false,
      where: userTeamsWhere,
    };

    // Only add search if we have text to search for
    if (searchText && searchText.trim()) {
      teamSearchParams.search = searchText;
    }

    const [users, userTeams] = await Promise.all([
      this.usersService.list(userSearchParams),
      includeTeams ? this.slackUserGroupsService.list(teamSearchParams) : Promise.resolve(undefined),
    ]);

    if (requestId !== this.lastRequestId) {
      return;
    }

    if (userTeams?.data) {
      userTeams?.data?.forEach((team) => {
        data.push({
          id: team.id,
          name: `${team.name} (Team)`,
          image: '',
          slackId: team.slackId,
          type: SUPPORT_STEP_ASSIGNEE_TYPES.TEAM,
          object: team,
        });
      });
    }

    if (users?.data) {
      users?.data?.forEach((user) => {
        data.push({
          name: formatUserName(user, this.connectedTeams || []),
          id: user.id,
          image: user.photo,
          email: user.email,
          type: SUPPORT_STEP_ASSIGNEE_TYPES.USER,
          object: user,
          slackId: user.slackId,
        });
      });
    }

    this.taggableUsers = data;

    if (this.taggableUsers.length) {
      this.highlightedItemIndex = 0;
      this.tippyService.show(this.tippyName);
      this.tippyService.getInstance(this.tippyName).setProps({
        getReferenceClientRect: () => this.editorWrapper.nativeElement.getBoundingClientRect(),
      });
    } else {
      this.tippyService.hide(this.tippyName);
    }
  }

  async searchChannels(searchText: string) {
    if (!this.enableSlackMentions) {
      return;
    }

    // If multiple teams and no team selected yet, show team selection first
    if (this.connectedTeams?.length > 1 && !this.selectedTeamForMentions) {
      this.searchTeams(searchText);
      return;
    }

    this.autocompleteSearchMode = 'channels';
    this.showingTeamSelection = false;

    try {
      // Use selected team or fall back to default team
      const selectedTeam = this.selectedTeamForMentions || this.connectedTeams?.[0];
      const connectionId = selectedTeam?.connectionId || selectedTeam?.id;
      const teamId = selectedTeam?.id;

      const { channels } = await this.slackService.getConnectedChannels(true, connectionId, teamId);

      // Filter channels based on search text
      const filteredChannels = channels.filter(
        (channel) =>
          !searchText ||
          channel.name.toLowerCase().includes(searchText.toLowerCase()) ||
          channel.displayName?.toLowerCase().includes(searchText.toLowerCase()),
      );

      // Convert to format expected by the UI
      this.taggableChannels = filteredChannels.map((channel) => ({
        id: channel.id,
        name: channel.name,
        displayName: channel.displayName || channel.name,
        isPrivate: channel.isPrivate,
        slackId: channel.id, // Use channel.id as slackId for mentions
      }));

      // Always show the tooltip, even if no channels found (so user can go back)
      this.highlightedItemIndex = this.taggableChannels.length > 0 ? 0 : -1;
      this.tippyService.show(this.tippyName);
      this.tippyService.getInstance(this.tippyName).setProps({
        getReferenceClientRect: () => this.editorWrapper.nativeElement.getBoundingClientRect(),
      });
    } catch (error) {
      console.error('Error searching channels:', error);
      this.taggableChannels = [];
      this.highlightedItemIndex = -1;
      // Keep tooltip open so user can go back even on error
      this.tippyService.show(this.tippyName);
      this.tippyService.getInstance(this.tippyName).setProps({
        getReferenceClientRect: () => this.editorWrapper.nativeElement.getBoundingClientRect(),
      });
    }
  }

  async selectTaggedUser(user: any) {
    if (!user) return;

    const displayLabel = user.name || user.email || 'Unknown User';
    const token = `<span data-type="mention" data-label="${displayLabel}" data-id="${user.id}|${user.slackId ?? ''}|${user.type}"></span> `;

    // Use taggableRange if available (from CustomMention extension), otherwise use keyCommandStartIndex (from manual trigger)
    const range = this.taggableRange || {
      from: this.keyCommandStartIndex,
      to: this.editor.state.selection.anchor,
    };
    this.editor.commands.deleteRange(range);
    this.editor.commands.insertContent(token, {
      updateSelection: true,
      parseOptions: {
        preserveWhitespace: 'full',
      },
    });
    this.hideAutocompleteTippy();
    this.emitChange();
  }

  async selectTaggedChannel(channel: any) {
    if (!channel) return;

    const range = {
      from: this.keyCommandStartIndex,
      to: this.editor.state.selection.anchor,
    };

    const token = `<span data-type="slack-channel-mention" data-slack-id="${channel.slackId}" class="mention-tag">#${channel.name}</span> `;
    this.editor.commands.deleteRange(range);
    this.editor.commands.insertContent(token, {
      updateSelection: true,
      parseOptions: {
        preserveWhitespace: 'full',
      },
    });
    this.hideAutocompleteTippy();
    this.emitChange();
  }

  async searchTeams(searchText: string) {
    if (!this.enableSlackMentions) {
      return;
    }

    this.autocompleteSearchMode = 'teams';
    this.showingTeamSelection = true;

    // Filter teams based on search text
    const filteredTeams = this.connectedTeams.filter(
      (team) => !searchText || team.name.toLowerCase().includes(searchText.toLowerCase()),
    );

    this.taggableTeams = filteredTeams;

    if (this.taggableTeams.length) {
      this.highlightedItemIndex = 0;
      this.tippyService.show(this.tippyName);
      this.tippyService.getInstance(this.tippyName).setProps({
        getReferenceClientRect: () => this.editorWrapper.nativeElement.getBoundingClientRect(),
      });
    } else {
      this.tippyService.hide(this.tippyName);
    }
  }

  async selectTeamForMentions(team: any) {
    if (!team) return;

    this.selectedTeamForMentions = team;
    this.showingTeamSelection = false;

    // Now search for channels in the selected team
    await this.searchChannels('');
  }

  goBackToTeamSelection() {
    this.selectedTeamForMentions = null;
    this.showingTeamSelection = true;
    this.searchTeams('');
  }

  async searchWorkspacesForUsers(searchText: string) {
    if (!this.enableSlackMentions) {
      return;
    }

    this.autocompleteSearchMode = 'users'; // Keep as 'users' but show workspace selection
    this.showingWorkspaceSelectionForUsers = true;

    // Filter connected teams based on search text (similar to channel selector)
    const filteredWorkspaces = this.connectedTeams.filter(
      (team) => !searchText || team.name.toLowerCase().includes(searchText.toLowerCase()),
    );

    this.availableWorkspacesForUsers = filteredWorkspaces;

    if (this.availableWorkspacesForUsers.length) {
      this.highlightedItemIndex = 0;
      this.tippyService.show(this.tippyName);
      this.tippyService.getInstance(this.tippyName).setProps({
        getReferenceClientRect: () => this.editorWrapper.nativeElement.getBoundingClientRect(),
      });
    } else {
      this.tippyService.hide(this.tippyName);
    }
  }

  async selectWorkspaceForUsers(workspace: any) {
    if (!workspace) return;

    this.selectedWorkspaceForUsers = workspace;
    this.showingWorkspaceSelectionForUsers = false;

    // Now search for users and user groups in the selected workspace
    await this.searchTaggableUsers('', true);
  }

  goBackToWorkspaceSelectionForUsers() {
    this.selectedWorkspaceForUsers = null;
    this.showingWorkspaceSelectionForUsers = true;
    this.searchWorkspacesForUsers('');
  }
}
